import { isAppointmentRescheduleRequested } from '@/lib/work-items/appointmentRescheduleState';
import type { DisplayMode, FeatureFlags, QuickAction, WorkItem, WorkflowStateRecord } from '../types';
import { getWorkItemTypeRegistry } from '@/config/work-items/configLoader';

/** @deprecated Fallback terminal status list. Use SSOT via configLoader.isTerminalStatus() when itemType is available. */
export const SMART_TASK_TERMINAL_ITEM_STATUSES = [
  'completed',
  'resolved',
  'rescheduled',
  'unresolved',
  'closed',
  'missed',
  'no_show',
  'cancelled',
  'failed',
  'skipped',
  'apt_resolved',
  'apt_partial',
  'apt_reschedule',
  'apt_cancelled',
] as const;

export type SmartTaskTerminalItemStatus = typeof SMART_TASK_TERMINAL_ITEM_STATUSES[number];

export type TemplateActionSource =
  | 'task'
  | 'job'
  | 'stop'
  | 'visit'
  | 'patrol'
  | 'work_order'
  | 'block';

export interface TemplateActionContext {
  displayMode: DisplayMode;
  allowedItemTypes: string[];
  requireTravel?: boolean;
}

export interface TemplateActionDefinition {
  key: string;
  sourceKey: string;
  source: TemplateActionSource;
  label: string;
  action: string;
  icon?: string;
  color?: string;
  config: Record<string, any>;
}

interface ResolvedTemplateActionAlias extends Partial<QuickAction> {
  action: string;
}

const FEATURE_SIGNAL_MAP: Record<string, Array<keyof FeatureFlags>> = {
  add_signature: ['signatureCapture'],
  create_capa: ['enableDocumentGeneration'],
  create_followup: ['enableFollowUpScheduling'],
  failed: ['enableFailedDelivery', 'routes', 'routeStopRecording'],
  failed_delivery: ['enableFailedDelivery', 'routes', 'routeStopRecording'],
  generate_document: ['enableDocumentGeneration'],
  generate_quote: ['enableQuoteGeneration'],
  get_signature: ['signatureCapture'],
  log_ncr: ['enableNCRReporting'],
  log_observation: ['enableDocumentGeneration'],
  log_reading: ['enableMeasurements'],
  log_time: ['enableTimeEntry'],
  open_route_picker: ['routes', 'routeStopRecording'],
  raise_followup: ['enableFollowUpScheduling'],
  raise_ncr: ['enableNCRReporting'],
  raise_quote: ['enableQuoteGeneration'],
  quality_check: ['enableQCInspection'],
  record_measurement: ['enableMeasurements'],
  record_vitals: ['enableMeasurements'],
  report_issue: ['enableReportIssue'],
  scan_component: ['qrScanning', 'barcodeScanning'],
  scan_job: ['qrScanning', 'barcodeScanning'],
  scan_package: ['enablePackageScanning'],
  schedule_follow_up: ['enableFollowUpScheduling'],
  skip: ['routes', 'routeStopRecording'],
  skip_stop: ['routes', 'routeStopRecording'],
  start_route: ['routes', 'routeStopRecording'],
  take_measurement: ['enableMeasurements'],
  take_photo: ['photoCapture'],
  take_after: ['photoCapture'],
  take_before: ['photoCapture'],
  view_bom: ['enableBOMViewer'],
  view_drawing: ['enableDrawingViewer'],
};

const CORE_WORKFLOW_LIKE_ACTIONS = new Set([
  'arrive_site',
  'check_in',
  'check_in_without_travel',
  'complete_job',
  'open_appointment_picker',
  'open_job_picker',
  'open_route_picker',
  'open_task_picker',
  'pause_work',
  'resume_work',
  'start_completing',
  'start_travel',
]);

function normalizeToken(value: unknown): string {
  return typeof value === 'string'
    ? value.trim().toLowerCase().replace(/[\s-]+/g, '_')
    : '';
}

function getSmartTaskDerivedTerminalStatus(statusSource?: Record<string, any> | null): string | null {
  if (!statusSource || typeof statusSource !== 'object') {
    return null;
  }

  const customFields = statusSource.custom_fields as Record<string, any> | null | undefined;
  const resolution = normalizeToken(
    statusSource.resolution_type
    ?? customFields?.resolution_type
    ?? customFields?.resolution
    ?? customFields?.completion_resolution,
  );
  const hasCompletionMarker = Boolean(
    statusSource.resolved_at
    || statusSource.completed_at
    || customFields?.resolved_at
    || customFields?.completed_at
    || customFields?.check_out_timestamp,
  );

  if (!hasCompletionMarker && !resolution) {
    return null;
  }

  if (resolution === 'rescheduled') {
    return 'rescheduled';
  }

  if (resolution === 'resolved') {
    return 'resolved';
  }

  if (resolution === 'unresolved' || resolution === 'partial') {
    return 'unresolved';
  }

  if (['cancelled', 'closed', 'missed', 'no_show', 'failed', 'skipped'].includes(resolution)) {
    return resolution;
  }

  return hasCompletionMarker ? normalizeToken(statusSource.status) || 'closed' : null;
}

function formatActionLabel(value: string): string {
  return value
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function inferPickerIntent(context: TemplateActionContext, preferred: 'job' | 'appointment' | 'task' | 'route' = 'job'): string {
  if (preferred === 'route') {
    return 'open_route_picker';
  }

  if (preferred === 'appointment') {
    return 'open_appointment_picker';
  }

  if (preferred === 'task') {
    return 'open_task_picker';
  }

  if (context.displayMode === 'appointment_centric') {
    return 'open_appointment_picker';
  }

  if (context.displayMode === 'task_centric' || context.displayMode === 'project_centric') {
    return 'open_task_picker';
  }

  return 'open_job_picker';
}

export function resolveTemplateActionAlias(
  rawKey: string,
  context: TemplateActionContext,
): ResolvedTemplateActionAlias | null {
  const normalizedKey = normalizeToken(rawKey);

  if (!normalizedKey) {
    return null;
  }

  switch (normalizedKey) {
    case 'start_job':
      return {
        action: inferPickerIntent(context, 'job'),
        label: 'Start Job',
        icon: 'Briefcase',
        color: '#2563eb',
      };

    case 'select_appointment':
    case 'start_appointment':
      return {
        action: inferPickerIntent(context, 'appointment'),
        label: 'Start Appointment',
        icon: 'Calendar',
        color: '#7c3aed',
      };

    case 'start_task':
      return {
        action: inferPickerIntent(context, 'task'),
        label: 'Start Task',
        icon: 'ClipboardList',
        color: '#0891b2',
      };

    case 'start_route':
      return {
        action: inferPickerIntent(context, 'route'),
        label: 'Start Route',
        icon: 'Route',
        color: '#0f766e',
      };

    case 'pause_job':
      return {
        action: 'pause_work',
        label: 'Pause Work',
        icon: 'Pause',
        color: '#f59e0b',
      };

    case 'resume_job':
    case 'resume_task':
      return {
        action: 'resume_work',
        label: 'Resume Work',
        icon: 'Play',
        color: '#2563eb',
      };

    case 'complete_task':
      return {
        action: 'start_completing',
        label: 'Complete Task',
        icon: 'CheckCircle',
        color: '#22c55e',
      };

    case 'complete':
    case 'complete_delivery':
    case 'complete_stop':
      return {
        action: 'start_completing',
        label: normalizedKey === 'complete_delivery'
          ? 'Complete Delivery'
          : normalizedKey === 'complete_stop'
            ? 'Complete Stop'
            : 'Complete',
        icon: 'CheckCircle',
        color: '#22c55e',
      };

    case 'scan_job':
      return {
        action: 'scan_qr',
        label: 'Scan Job',
        icon: 'QrCode',
        color: '#0891b2',
      };

    case 'scan_component':
      return {
        action: 'scan_qr',
        label: 'Scan Component',
        icon: 'QrCode',
        color: '#0891b2',
      };

    case 'raise_ncr':
      return {
        action: 'log_ncr',
        label: 'Log NCR',
        icon: 'FileWarning',
        color: '#dc2626',
      };

    case 'quality_check':
      return {
        action: 'run_qc',
        label: 'Quality Check',
        icon: 'ClipboardCheck',
        color: '#0f766e',
      };

    case 'record_measurement':
      return {
        action: 'take_measurement',
        label: 'Measure',
        icon: 'Ruler',
        color: '#0891b2',
      };

    case 'arrive':
    case 'arrive_stop':
      return {
        action: 'arrive_site',
        label: 'Arrive',
        icon: 'MapPin',
        color: '#10b981',
      };

    case 'failed':
      return {
        action: 'failed_delivery',
        label: 'Failed Delivery',
        icon: 'XCircle',
        color: '#dc2626',
        requiresActiveJob: true,
        requiresCheckedIn: true,
      };

    case 'skip':
    case 'skip_stop':
      return {
        action: 'skip_stop',
        label: 'Skip Stop',
        icon: 'SkipForward',
        color: '#64748b',
        requiresActiveJob: true,
        requiresCheckedIn: true,
      };

    default:
      return null;
  }
}

export function collectTemplateActionSignals(templateConfig: Record<string, any> | null | undefined): string[] {
  if (!templateConfig || typeof templateConfig !== 'object') {
    return [];
  }

  const signals = new Set<string>();
  const addSignal = (value: unknown) => {
    const normalized = normalizeToken(value);
    if (normalized) {
      signals.add(normalized);
    }
  };

  const rawQuickActions = Array.isArray(templateConfig.quick_actions)
    ? templateConfig.quick_actions
    : Array.isArray(templateConfig.quickActions)
      ? templateConfig.quickActions
      : [];

  for (const entry of rawQuickActions) {
    if (typeof entry === 'string') {
      addSignal(entry);
      continue;
    }

    if (entry && typeof entry === 'object') {
      addSignal((entry as Record<string, any>).action);
      addSignal((entry as Record<string, any>).key);
    }
  }

  const actionMaps = [
    templateConfig.task_actions,
    templateConfig.job_actions,
    templateConfig.stop_actions,
    templateConfig.visit_actions,
    templateConfig.patrol_actions,
    templateConfig.work_order_actions,
    templateConfig.block_actions,
  ];

  for (const actionMap of actionMaps) {
    if (!actionMap || typeof actionMap !== 'object' || Array.isArray(actionMap)) {
      continue;
    }

    for (const key of Object.keys(actionMap)) {
      addSignal(key);
    }
  }

  if (templateConfig.enable_routes === true) {
    signals.add('start_route');
  }

  return Array.from(signals);
}

export function inferFeatureFlagsFromTemplateSignals(
  signals: Iterable<string>,
  templateConfig?: Record<string, any> | null,
): Partial<FeatureFlags> {
  const flags: Partial<FeatureFlags> = {};
  const normalizedSignals = new Set(Array.from(signals, (signal) => normalizeToken(signal)).filter(Boolean));

  for (const signal of normalizedSignals) {
    const mappedFlags = FEATURE_SIGNAL_MAP[signal] || [];
    for (const featureFlag of mappedFlags) {
      flags[featureFlag] = true;
    }
  }

  if (templateConfig?.enable_routes === true) {
    flags.routes = true;
    flags.routeStopRecording = true;
  }

  return flags;
}

export function mergeTemplateActionMaps(
  maps: Partial<Record<TemplateActionSource, Record<string, any> | null | undefined>>,
  context: TemplateActionContext,
): TemplateActionDefinition[] {
  const merged = new Map<string, TemplateActionDefinition>();

  for (const [source, actionMap] of Object.entries(maps) as Array<[TemplateActionSource, Record<string, any> | null | undefined]>) {
    if (!actionMap || typeof actionMap !== 'object' || Array.isArray(actionMap)) {
      continue;
    }

    for (const [rawKey, rawConfig] of Object.entries(actionMap)) {
      const alias = resolveTemplateActionAlias(rawKey, context);
      const action = alias?.action || normalizeToken(rawKey);
      if (!action) {
        continue;
      }

      const actionConfig = rawConfig && typeof rawConfig === 'object' && !Array.isArray(rawConfig)
        ? rawConfig as Record<string, any>
        : {};
      const dedupeKey = action;
      const existing = merged.get(dedupeKey);

      merged.set(dedupeKey, {
        key: action,
        sourceKey: rawKey,
        source,
        label: typeof actionConfig.label === 'string' && actionConfig.label.trim().length > 0
          ? actionConfig.label.trim()
          : alias?.label || formatActionLabel(rawKey),
        action,
        icon: typeof actionConfig.icon === 'string' && actionConfig.icon.trim().length > 0
          ? actionConfig.icon.trim()
          : alias?.icon || existing?.icon,
        color: typeof actionConfig.color === 'string' && actionConfig.color.trim().length > 0
          ? actionConfig.color.trim()
          : alias?.color || existing?.color,
        config: {
          ...(existing?.config || {}),
          ...actionConfig,
          source_key: rawKey,
          source,
          canonical_action: action,
        },
      });
    }
  }

  return Array.from(merged.values());
}

export function getPickerModeForActionIntent(action: string): 'job' | 'appointment' | 'task' | 'route' | null {
  switch (normalizeToken(action)) {
    case 'open_job_picker':
      return 'job';
    case 'open_appointment_picker':
      return 'appointment';
    case 'open_task_picker':
      return 'task';
    case 'open_route_picker':
      return 'route';
    default:
      return null;
  }
}

export function isCoreWorkflowLikeTemplateAction(action: string): boolean {
  return CORE_WORKFLOW_LIKE_ACTIONS.has(normalizeToken(action));
}

export function isSmartTaskTerminalStatus(status?: string | null, itemType?: string | null): boolean {
  const normalizedStatus = normalizeToken(status);
  if (!normalizedStatus) return false;
  if (itemType) {
    try {
      const registry = getWorkItemTypeRegistry();
      if (registry.isTerminalStatus(itemType, normalizedStatus)) {
        return true;
      }
    } catch { /* fallback below */ }
  }
  // Try operational fallback when itemType is unknown
  try {
    const registry = getWorkItemTypeRegistry();
    if (registry.isOperationalTerminalStatus(normalizedStatus)) {
      return true;
    }
  } catch { /* fallback below */ }
  return SMART_TASK_TERMINAL_ITEM_STATUSES.includes(normalizedStatus as SmartTaskTerminalItemStatus);
}

export function getSmartTaskVisibleStatus(
  status?: string | null,
  statusSource?: Record<string, any> | null,
): string {
  const normalizedStatus = normalizeToken(status);
  const derivedTerminalStatus = getSmartTaskDerivedTerminalStatus(statusSource);

  if (derivedTerminalStatus) {
    return derivedTerminalStatus;
  }

  if (normalizedStatus === 'unresolved' && isAppointmentRescheduleRequested({
    status,
    item_type: statusSource?.item_type,
    resolution_type: statusSource?.resolution_type,
    custom_fields: statusSource?.custom_fields ?? statusSource,
  })) {
    return 'closed';
  }

  if (normalizedStatus === 'rescheduled') {
    return 'rescheduled';
  }

  if (!['ai_review', 'ai_on_hold'].includes(normalizedStatus)) {
    return normalizedStatus;
  }

  return 'closed';
}

export function getSmartTaskWorkflowDisplayStatus(
  item: Pick<WorkItem, 'id' | 'status' | 'custom_fields'>,
  workflowState?: Pick<WorkflowStateRecord, 'current_state' | 'active_work_item_id' | 'traveling_to_work_item_id'> | null,
): string {
  const baseStatus = getSmartTaskVisibleStatus(item.status, item as Record<string, any>);

  if (!workflowState) {
    return baseStatus;
  }

  const normalizedState = normalizeToken(workflowState.current_state);
  const itemId = String(item.id);
  const activeWorkItemId = workflowState.active_work_item_id != null
    ? String(workflowState.active_work_item_id)
    : null;
  const travelingToWorkItemId = workflowState.traveling_to_work_item_id != null
    ? String(workflowState.traveling_to_work_item_id)
    : null;

  if (travelingToWorkItemId === itemId) {
    if (normalizedState === 'travel_paused') {
      return 'travel_paused';
    }

    if (normalizedState === 'traveling') {
      return 'traveling';
    }
  }

  if (activeWorkItemId === itemId) {
    if (normalizedState === 'arrived') {
      return 'arrived';
    }

    if (normalizedState === 'paused') {
      return 'paused';
    }

    if (normalizedState === 'checked_in' || normalizedState === 'completing') {
      return 'working';
    }
  }

  return baseStatus;
}

export function formatSmartTaskStatusLabel(status?: string | null, itemType?: string | null): string {
  const normalizedStatus = normalizeToken(status);

  if (!normalizedStatus) return '';

  if (itemType) {
    try {
      const registry = getWorkItemTypeRegistry();
      const label = registry.getStatusLabel(itemType, normalizedStatus);
      if (label) return label;
    } catch { /* fallback below */ }
  }

  // Try operational generic fallback for workflow statuses
  try {
    const registry = getWorkItemTypeRegistry();
    const opDef = registry.getOperationalStatusDefinition(normalizedStatus);
    if (opDef?.label) return opDef.label;
  } catch { /* fallback below */ }

  switch (normalizedStatus) {
    case 'checked_in':
    case 'in_progress':
    case 'working':
      return 'In Progress / Working';
    case 'on_route':
    case 'en_route':
      return 'On Route / Travelling';
    default:
      return formatActionLabel(normalizedStatus);
  }
}
